import axios from "axios";
import { Authorization } from "../../Context/Authorization";

export const fetchCircles = async (Api, setCircleData, setLoading) => {
  setLoading(true);
  try {
    const config = Authorization();
    const response = await axios.get(`${Api}/api/circles`, config);
    const data = response.data;
    if (Array.isArray(data)) {
      setCircleData(data);
    } else {
      console.error("API did not return an array", data);
    }
  } catch (error) {
    console.error("Error fetching circles:", error);
  }
  setLoading(false);
};

export const fetchDivisionsByCircle = async (
  Api,
  circleId,
  setDivisionData,
  setLoading
) => {
  setLoading(true);
  try {
    const config = Authorization();
    const response = await axios.post(
      `${Api}/api/divisions/divisions-by-circle`,
      { circleId },
      config
    );
    setDivisionData(response.data);
  } catch (error) {
    console.error("Error fetching divisions:", error);
  }
  setLoading(false);
};

export const fetchRangesByDivision = async (
  Api,
  divisionId,
  setRangeData,
  setLoading
) => {
  setLoading(true);
  try {
    const config = Authorization();
    const response = await axios.post(
      `${Api}/api/ranges/ranges-by-division`,
      { divisionId },
      config
    );
    setRangeData(response.data);
  } catch (error) {
    console.error("Error fetching ranges:", error);
  }
  setLoading(false);
};

export const fetchVfcByRange = async (Api,
  rangeId,
  setVfcData,
  setLoading,
  jfmcTypeId) => {
  setLoading(true);
  try {
    const config = Authorization();
    const response = await axios.post(
      `${Api}/api/vfcs/vfcs-by-range`,
      {
        rangeId,
        jfmcTypeId, // ✅ added
      },
      config
    );

    setVfcData(response.data.vfcs);
  } catch (error) {
    console.error("Error fetching VFCs:", error);
  }
  setLoading(false);
};

export const fetchOpeningBalance = async (
  Api,
  vfcId,
  date,
  setOpeningBalance,
  setError,
  setLoading
) => {
  setLoading(true);
  try {
    const response = await fetch(`${Api}/api/closing-balance/openingBalance`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ vfcId, date }),
    });

    if (!response.ok) {
      throw new Error("Failed to fetch opening balance");
    }

    const data = await response.json();

    let balanceObj = { cash: 0, bank: 0 };

    // ✅ Handle multiple response formats
    if (data?.data && Array.isArray(data.data) && data.data.length > 0) {
      // Case 1: { success: true, data: [ { cash, bank } ] }
      balanceObj = data.data[0];
    } else if (Array.isArray(data) && data.length > 0) {
      // Case 2: [ { cash, bank } ]
      balanceObj = data[0];
    } else if (data && typeof data === "object" && "cash" in data && "bank" in data) {
      // Case 3: { cash: 0, bank: 874438 }
      balanceObj = data;
    }

    // ✅ Update React state
    setOpeningBalance({
      cash: balanceObj.cash || 0,
      bank: balanceObj.bank || 0,
    });

    console.log("✅ Fetched Opening Balance (Final):", balanceObj);

    // ✅ Return to caller for immediate use
    return {
      cash: balanceObj.cash || 0,
      bank: balanceObj.bank || 0,
    };
  } catch (error) {
    console.error("Error fetching opening balance:", error.message);
    setError(error.message);
    return { cash: 0, bank: 0 };
  } finally {
    setLoading(false);
  }
};



export const fetchTransactions = async (
  Api,
  vfcId,
  date,
  setTransactions,
  setError,
  setLoading
) => {
  setLoading(true);
  try {
    const config = Authorization();
    const response = await axios.post(
      `${Api}/api/transactions/get-transaction-by-vfc-date`,
      { vfcId, date },
      config
    );
    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to fetch transactions");
    }
    setTransactions(response.data.data);
  } catch (error) {
    console.error("Error fetching transactions:", error.message);
    setError(error.message);
  }
  setLoading(false);
};


export const fetchLoansByVfcAndDate = async (
  Api,
  vfcId,
  date,
  setLoans,
  setError
) => {
  try {
    const config = Authorization();
    const response = await axios.post(
      `${Api}/api/loans/get-loans-by-vfc-date`,
      { vfcId, date },
      config
    );
    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to fetch loans");
    }
    const loans =
      response.data && Array.isArray(response.data.data)
        ? response.data.data
        : [];
    setLoans(loans);
  } catch (error) {
    console.error("Error fetching loans:", error.message);
    setLoans([]);
    setError(error.message);
  }
};

export const fetchLoansCollectionByVfc = async (
  Api,
  selectedVfc,
  date,
  setLoanDetails,
  setLoading
) => {
  setLoading(true);
  try {
    const config = Authorization();
    const response = await axios.post(
      `${Api}/api/loan-collections/get-loancollection-by-vfc-date`,
      { vfcId: selectedVfc, date },
      config
    );

    // Ensure `data` exists and is an array
    const loansCollection =
      response.data && Array.isArray(response.data.data)
        ? response.data.data
        : [];

    setLoanDetails(loansCollection);
  } catch (error) {
    console.error("Error fetching loans by VFC:", error);
    setLoanDetails([]); // Set empty array on error to prevent crashes
  }
  setLoading(false);
};

export const handleEndOfBusiness = async (
  Api,
  selectedVfcId,
  selectDate,
  closingBalance,
  setError,
  setLoading,
  onSuccess
) => {
  if (!selectedVfcId) {
    setError("Please select a VFC before ending the business day.");
    return;
  }
  if (!selectDate) {
    setError("Please select a date before ending the business day.");
    return;
  }
  const closingBalanceData = {
    vfcId: selectedVfcId,
    date: selectDate,
    cash: closingBalance.cash,
    bank: closingBalance.bank,
    dateOfExecution: new Date(),
  };
  try {
    setLoading(true);
    const config = Authorization();
    const response = await axios.post(
      `${Api}/api/closing-balance`,
      closingBalanceData,
      config
    );
    if (response.status === 200 || response.status === 201) {
      onSuccess();
    } else {
      setError(response.data.message || "Failed to end business day.");
    }
  } catch (err) {
    console.error("Error ending business:", err);
    setError("An error occurred while ending the business day.");
  } finally {
    setLoading(false);
  }
};
export const formatDate = (date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};
